<template>
	<div class="dropdown" :class="{ 'is-active': active }" v-click-outside-element="closeDropdown">
		<div class="dropdown-trigger" @click="toggleDropdown">
			<button class="button" :class="{ 'is-cyan': selection.length }" aria-haspopup="true" aria-controls="dropdown-menu">
				<span v-text="label"></span>

				<span class="icon">
					<span></span>
				</span>
			</button>
		</div>

		<div class="dropdown-menu" id="dropdown-menu" role="menu">
			<div class="dropdown-content">
				<span
					v-for="(option, index) in options"
					:key="index"
					class="dropdown-item"
					:class="{ 'is-active': selection.includes(option.key) }"
				>
					<label class="checkbox">
						<input type="checkbox" :checked="selection.includes(option.key)" @change="select($event, option.key)" />
						<span v-html="getTermLabel(option)" :class="{ 'has-text-grey-lighter': isTermGreyedOut(option) }"></span>
					</label>
				</span>
			</div>
		</div>
	</div>
</template>

<script>

// import analytics from 'Scripts/analytics';

export default {
	data() {
		return {
			/* Whether the dropdown box is visible or not */
			active: false,

			/* User-facing labels for each taxonomy */
			labels: {
				topics: 'Topic',
				experiences: 'Experience',
				roles: 'Role',
			},
		};
	},

	computed: {
		/* List of available terms */
		terms() {
			return this.$store[this.taxonomy] || {};
		},

		/* Current selection */
		selection: {
			get() {
				return this.$store.filter[this.taxonomy] || [];
			},
			set(value) {
				this.setFilter(value, this.taxonomy);
			},
		},

		/* User-facing dropdown label */
		label() {
			/* Taxonomy name, if known */
			let label = this.labels[this.taxonomy] || 'Filter';

			/* Selected count in parenthesis, if any */
			if (this.selection.length > 0) {
				label += ` (${this.selection.length})`;
			}

			return label;
		},

		/* List of available options, in alphabetical order */
		options() {
			const options = [];

			/* Convert from object to array */
			for (const key in this.terms) {
				options.push({ key, term: this.terms[key] });
			}

			/* Sort alphabetically */
			return options.sort(this.sortTerms);
		},
	},

	methods: {
		/* Show or hide the dropdown box */
		toggleDropdown() {
			this.active = !this.active;
		},

		/* Hide the dropdown box */
		closeDropdown() {
			this.active = false;
		},

		/* Handle selecting an option */
		select(event, key) {
			/* Update selection based on checkbox */
			if (event.target.checked && !this.selection.includes(key)) {
				// this.trackAnalyticsEvent(key, 'select');
				this.selection.push(key);
			} else if (!event.target.checked && this.selection.includes(key)) {
				// this.trackAnalyticsEvent(key, 'deselect');
				this.selection = this.selection.filter(e => e !== key);
			}
		},

		/* Get number of courses for particular option */
		getPotentialCourses(option) {
			// When checking potential matches, we want to show courses that match this option
			// AND any currently selected filters in other taxonomies
			const potentialTopics = this.taxonomy === 'topics' ? [option.key] : this.$store.filter.topics;
			const potentialRoles = this.taxonomy === 'roles' ? [option.key] : this.$store.filter.roles;
			const potentialExperiences = this.taxonomy === 'experiences' ? [option.key] : this.$store.filter.experiences;

			const filteredCourses = this.getFilteredCourses(potentialRoles, potentialTopics, potentialExperiences);

			return filteredCourses;
		},

		/* Display label for each option */
		getTermLabel(option) {
			const potentialCourses = this.getPotentialCourses(option);
			return `${option.term} (${potentialCourses.length})`;
		},

		isTermGreyedOut(option) {
			const potentialCourses = this.getPotentialCourses(option);
			return potentialCourses.length === 0;
		},
	},

	props: {
		/* Name of the taxonomy we're selecting for */
		taxonomy: {
			type: String,
			required: true,
		},
	},
};

</script>

<style lang="scss" scoped>
@import '@gearset/web-core/styles/includes/variables';

.dropdown-content {
	max-width: 260px;
	text-align: left;
	padding-block: 1rem;

	label {
		display: flex;

		span {
			color: $navy;
			font-weight: normal;
			line-height: 1.35;
		}
	}

	.checkbox input[type="checkbox"] {
		margin-right: 10px;
		appearance: none;
		width: 18px;
		height: 18px;
		border: 2px solid #61707B;
		border-radius: 0;
		position: relative;
		cursor: pointer;
		vertical-align: middle;

		&:checked {
			background-color: $cyan;
			border-color: $cyan;

			&::after {
				content: '';
				position: absolute;
				left: 5px;
				top: 0;
				width: 5px;
				height: 12px;
				border: solid $navy;
				border-width: 0 2px 2px 0;
				transform: rotate(45deg);
			}
		}
	}
}

.dropdown-menu {
	left: 5px;
}

.dropdown-item {
	font-size: 1rem;
	padding-inline: 1.5rem;
}

.dropdown-item.is-active {
	color: $navy;
	font-weight: $weight-semibold;
	background: rgba($grey-lighter, .1);
}

.dropdown-trigger .icon {
	transition: transform .2s ease-in-out;
	width: 1em;
	height: 1em;

	.is-active & {
		transform: rotate(-180deg);
	}

	span {
		display: inline-block;
		width: 1em;
		height: 1em;
		mask: url('/images/icons/ui/arrow-down.svg') no-repeat;
		mask-position: center center;
		mask-size: 100%;
		background-color: $grey-lighter;
	}
}

.dropdown-trigger .button.is-cyan {
	background: $cyan;
	color: $navy;
	border-color: $cyan;

	.icon span {
		background: $navy;
	}
}

.dropdown-trigger .button {
	font-size: $size-5;
	text-transform: capitalize;
	padding: 6px 15px 6px 20px;
	justify-content: space-between;
	width: 260px;
	border: solid 1px $grey-lighter;

	span {
		font-weight: normal;
	}
}
</style>
